
import React, { useRef, useEffect } from "react";
import { Message } from "@/services/messages";
import { useAuth } from "@/contexts/auth";
import { MessageCircle } from "lucide-react";
import MessageItem from "./MessageItem";
import { createLogger } from "@/services/utils/loggingUtils";

const logger = createLogger('ConversationBody');

interface ConversationBodyProps {
  messages: Message[];
  otherUsername: string;
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}

const ConversationBody: React.FC<ConversationBodyProps> = ({
  messages,
  otherUsername,
  isLoading,
  hasMore,
  onLoadMore
}) => {
  const { user } = useAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Effect to scroll to bottom on new messages and initial load
  useEffect(() => {
    if (messagesEndRef.current && messages.length > 0) {
      const container = scrollContainerRef.current;
      
      // Always scroll to bottom on initial load (when scrollTop is 0 and no scroll has happened)
      const isInitialLoad = container && container.scrollTop === 0 && container.scrollHeight > container.clientHeight;
      
      // Check if we're at the bottom already
      const isAtBottom = container &&
        (container.scrollHeight - container.scrollTop <= container.clientHeight + 100);
      
      // Check if there's a new message (optimistic or just sent)
      const hasNewMessage = messages.some(m => m.id.startsWith('temp-'));
      
      if (isInitialLoad || hasNewMessage || isAtBottom) {
        messagesEndRef.current.scrollIntoView({ behavior: isInitialLoad ? 'instant' : 'smooth' });
      }
    }
  }, [messages]);
  
  // Handle scroll to top for loading more messages
  useEffect(() => {
    const handleScroll = () => {
      const container = scrollContainerRef.current;
      if (!container || isLoading || !hasMore) return;
      
      // Load more when we scroll near the top
      if (container.scrollTop <= 20) {
        onLoadMore();
      }
    };
    
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }
    
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isLoading, hasMore, onLoadMore]);
  
  // If no messages, show empty state
  if (messages.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <MessageCircle className="h-16 w-16 mb-6 text-muted-foreground opacity-70" />
        <p className="text-muted-foreground text-lg">No messages yet.</p>
        <p className="text-muted-foreground">Start the conversation!</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div 
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto"
      >
        {/* Load more indicator */}
        {hasMore && (
          <div className="flex justify-center p-3 sticky top-0 z-10 bg-background/80 backdrop-blur-sm">
            <button 
              onClick={onLoadMore} 
              disabled={isLoading}
              className="text-sm text-primary hover:underline disabled:opacity-50 px-4 py-2"
            >
              {isLoading ? 'Loading...' : 'Load older messages'}
            </button>
          </div>
        )}
        
        {/* Messages */}
        <div className="space-y-1 px-4 pb-4">
          {messages.map((message, index) => {
            const isOwnMessage = message.sender_id === user?.id;
            
            // Group messages from the same user
            const isPreviousSameSender = index > 0 && messages[index - 1].sender_id === message.sender_id;
            const isNextSameSender = index < messages.length - 1 && messages[index + 1].sender_id === message.sender_id;
            
            return (
              <div 
                key={message.id} 
                className={`${isPreviousSameSender ? 'pt-0.5' : 'pt-2'} ${isNextSameSender ? 'pb-0.5' : 'pb-2'}`}
              >
                <MessageItem
                  message={message}
                  isOwnMessage={isOwnMessage}
                  otherUsername={otherUsername}
                  isGrouped={isPreviousSameSender}
                />
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      </div>
    </div>
  );
};

export default React.memo(ConversationBody);
